import { makeAutoObservable } from "mobx";

export type LunchBreakDecision = "keep" | "delete";

export interface LunchBreakPromptContext {
  /** Total work hours for the day (excluding the break). */
  workHours: number;
}

/**
 * Global, promise-based prompt shown when a working-hours edit would leave a
 * lunch break on a day whose work time is below the legal threshold. The
 * backend no longer auto-deletes the break; instead we ask the user to keep or
 * delete it. `requestDecision` opens the dialog and resolves once the user
 * picks. Singleton, mirroring the notificationStore pattern.
 */
class LunchBreakPromptStore {
  open = false;
  context: LunchBreakPromptContext | null = null;
  resolver: ((decision: LunchBreakDecision) => void) | null = null;

  constructor() {
    makeAutoObservable(this, { resolver: false });
  }

  requestDecision(context: LunchBreakPromptContext): Promise<LunchBreakDecision> {
    this.context = context;
    this.open = true;
    return new Promise<LunchBreakDecision>((resolve) => {
      this.resolver = resolve;
    });
  }

  private settle(decision: LunchBreakDecision) {
    const resolve = this.resolver;
    this.resolver = null;
    this.context = null;
    this.open = false;
    resolve?.(decision);
  }

  chooseKeep() {
    this.settle("keep");
  }

  chooseDelete() {
    this.settle("delete");
  }
}

const lunchBreakPromptStore = new LunchBreakPromptStore();
export default lunchBreakPromptStore;
